import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Leaf, Trash2, ShoppingBag, Calendar, HelpCircle, CheckCircle2, XCircle, ArrowRight, Recycle, AlertCircle, Package } from "lucide-react";

/**
 * Public per-estate recycling guide. No auth. Mobile-first.
 * Route: /guide/:slug
 */
export default function ResidentGuide() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, guide: null, client: null, error: null });

  useEffect(() => {
    axios.get(`${API}/guides/public/${slug}`)
      .then((r) => setState({ loading: false, ...r.data, error: null }))
      .catch((err) => setState({ loading: false, guide: null, client: null, error: err?.response?.status || "err" }));
  }, [slug]);

  useEffect(() => {
    if (state.client?.name) document.title = `${state.client.name} — Recycling guide`;
  }, [state.client]);

  if (state.loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bone)" }}>
      <div className="text-neutral-500">Loading…</div>
    </div>;
  }
  if (state.error || !state.guide) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--bone)" }}>
        <div className="max-w-md text-center">
          <div className="hbt-label mb-3">Guide unavailable</div>
          <h1 className="font-display font-semibold text-2xl mb-3">We couldn&apos;t find this guide</h1>
          <p className="text-neutral-500 text-sm">The recycling guide for this estate may have been moved or turned off. If you scanned a QR code from your bin, please contact your estate office.</p>
          <Link to="/" className="btn-primary inline-flex items-center gap-2 mt-6"><ArrowRight className="w-4 h-4" /> Visit hbtwaste.com</Link>
        </div>
      </div>
    );
  }

  const { guide, client, last_month, this_month, streams_compare } = state;
  const streamIcon = (key) => key === "recyclable" ? Leaf : Trash2;

  const codeMeta = (action) => {
    if (action === "recycle") return { label: "Green bag", tone: "#2C6E49", chipBg: "rgba(44,110,73,0.12)", Icon: CheckCircle2 };
    if (action === "check") return { label: "Ask us", tone: "#B58900", chipBg: "rgba(181,137,0,0.15)", Icon: AlertCircle };
    return { label: "Black bag", tone: "#4A4D4A", chipBg: "rgba(74,77,74,0.15)", Icon: XCircle };
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bone)" }} data-testid="resident-guide-page">
      {/* Hero with layered gradient + SVG leaf pattern */}
      <div className="relative overflow-hidden" style={{ color: "var(--bone)" }}>
        {/* Photo background — neat segregated recycling bins in an estate setting */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1611284446314-60a58ac0deb9?fm=jpg&q=70&w=2400&auto=format&fit=crop")' }}
          aria-hidden="true"
        />
        {/* Dark gradient overlay so the hero copy stays legible */}
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(135deg, rgba(15,42,34,0.85) 0%, rgba(26,54,45,0.78) 45%, rgba(44,110,73,0.55) 100%)" }}
          aria-hidden="true"
        />
        {/* Subtle botanical accent (kept from the old design for texture) */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-40" viewBox="0 0 800 400" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          <path d="M -20 240 C 60 260, 100 340, 60 400 C 0 380, -30 320, -20 240 Z" fill="rgba(44,110,73,0.35)" />
        </svg>
        {/* Content */}
        <div className="relative max-w-3xl mx-auto px-6 py-16 md:py-20">
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full text-[11px] uppercase tracking-wider" style={{ background: "rgba(246,241,231,0.15)", color: "var(--bone)" }}>
            <Recycle className="w-3 h-3" /> {client.name}
          </div>
          <h1 className="font-display font-semibold text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-2xl" data-testid="guide-hero-title">{guide.hero_title}</h1>
          {guide.hero_stat && (
            <p className="mt-6 text-lg md:text-xl max-w-xl leading-relaxed" style={{ color: "rgba(246,241,231,0.9)" }}>{guide.hero_stat}</p>
          )}
          <div className="mt-8 flex items-center gap-3 text-xs" style={{ color: "rgba(246,241,231,0.65)" }}>
            <span className="pill" style={{ background: "rgba(246,241,231,0.15)", color: "var(--bone)" }}>Guide by HBT Waste Management</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 md:py-12 space-y-8">
        {/* Bag cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="guide-streams">
          {(guide.streams || []).map((s) => {
            const Icon = streamIcon(s.key);
            return (
              <div key={s.key} className="hbt-card overflow-hidden" data-testid={`stream-${s.key}`}>
                <div className="p-5 text-white" style={{ background: s.color }}>
                  <div className="flex items-center gap-3">
                    <Icon className="w-6 h-6" />
                    <div className="font-display font-semibold text-lg leading-tight">{s.label}</div>
                  </div>
                </div>
                <div className="p-5 space-y-4">
                  <div>
                    <div className="hbt-label mb-2 flex items-center gap-1.5" style={{ color: s.color }}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> Yes, put in
                    </div>
                    <ul className="space-y-1.5 text-sm">
                      {s.accepted.map((it, i) => <li key={i} className="flex items-start gap-2"><span className="mt-1.5 w-1 h-1 rounded-full bg-neutral-500 shrink-0" />{it}</li>)}
                    </ul>
                  </div>
                  <div>
                    <div className="hbt-label mb-2 flex items-center gap-1.5 text-neutral-500">
                      <XCircle className="w-3.5 h-3.5" /> No, keep out
                    </div>
                    <ul className="space-y-1.5 text-sm text-neutral-600">
                      {s.rejected.map((it, i) => <li key={i} className="flex items-start gap-2"><span className="mt-1.5 w-1 h-1 rounded-full bg-neutral-400 shrink-0" />{it}</li>)}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Plastic codes 1-7 */}
        {guide.plastic_codes?.length > 0 && (
          <div className="hbt-card p-6" data-testid="guide-plastic-codes">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(26,54,45,0.08)" }}>
                <Recycle className="w-5 h-5" style={{ color: "var(--forest)" }} />
              </div>
              <div>
                <div className="hbt-label">Confused? Check the number</div>
                <div className="font-display font-semibold text-xl mt-1">Plastic codes explained</div>
                <p className="text-xs text-neutral-500 mt-1">Turn the container over and look for the small triangle with a number inside. That number tells you which bag it belongs in.</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {guide.plastic_codes.map((c) => {
                const m = codeMeta(c.action);
                return (
                  <div key={c.code} className="border rounded-lg p-4 flex gap-3" style={{ borderColor: "var(--border)" }} data-testid={`code-${c.code}`}>
                    <div className="shrink-0 relative">
                      <svg viewBox="0 0 48 48" className="w-11 h-11">
                        <path d="M24 6 L42 38 L6 38 Z" fill="none" stroke={m.tone} strokeWidth="2.5" strokeLinejoin="round" />
                        <text x="24" y="34" textAnchor="middle" fontSize="15" fontWeight="700" fill={m.tone} fontFamily="ui-sans-serif, system-ui, sans-serif">{c.code}</text>
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{c.name}</span>
                        <span className="pill text-[10px]" style={{ background: m.chipBg, color: m.tone }}>
                          <m.Icon className="w-2.5 h-2.5 inline mr-1" /> {m.label}
                        </span>
                      </div>
                      <p className="text-xs text-neutral-600 mt-1 leading-relaxed">{c.examples}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 p-3 rounded text-xs" style={{ background: "rgba(181,137,0,0.10)", color: "#8A6900" }}>
              <strong>Simple rule:</strong> if the number is 1, 2, 4 or 5 — green bag (recycle). Anything else — black bag. If in doubt, black bag is always safe.
            </div>
          </div>
        )}

        {/* Bag supply */}
        <div className="hbt-card p-6" data-testid="guide-bag-supply">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(26,54,45,0.08)" }}>
              <ShoppingBag className="w-5 h-5" style={{ color: "var(--forest)" }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="hbt-label">Where to get green bags</div>
              <div className="font-display font-semibold text-lg mt-1">{guide.bag_supply?.location || "Estate office"}</div>
              <div className="text-sm text-neutral-600 mt-2 space-y-1">
                {guide.bag_supply?.hours && <div>{guide.bag_supply.hours}</div>}
                {guide.bag_supply?.price && <div>{guide.bag_supply.price}</div>}
                {guide.bag_supply?.notes && <div className="text-neutral-500 text-xs mt-2 italic">{guide.bag_supply.notes}</div>}
              </div>
            </div>
          </div>
        </div>

        {/* Bulky items */}
        {guide.bulky_items && (
          <div className="hbt-card p-6" data-testid="guide-bulky-items">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(44,110,73,0.12)" }}>
                <Package className="w-5 h-5" style={{ color: "#2C6E49" }} />
              </div>
              <div className="flex-1">
                <div className="hbt-label">Big items (TV boxes, furniture packaging)</div>
                <p className="text-sm text-neutral-700 mt-2 leading-relaxed">{guide.bulky_items}</p>
              </div>
            </div>
          </div>
        )}

        {/* Collection days */}
        {guide.collection_days && (
          <div className="hbt-card p-6" data-testid="guide-collection-days">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(85,124,85,0.15)" }}>
                <Calendar className="w-5 h-5" style={{ color: "#2C6E49" }} />
              </div>
              <div className="flex-1">
                <div className="hbt-label">Collection days</div>
                <p className="text-sm text-neutral-700 mt-2 leading-relaxed">{guide.collection_days}</p>
              </div>
            </div>
          </div>
        )}

        {/* This month vs last month — by stream (impact of sorting at home) */}
        {streams_compare && streams_compare.length > 0 && (() => {
          const hasData = streams_compare.some((r) => r.this_kg > 0 || r.last_kg > 0);
          if (!hasData) return null;
          const maxKg = Math.max(1, ...streams_compare.map((r) => Math.max(r.this_kg, r.last_kg)));
          const sumThis = streams_compare.reduce((a, r) => a + r.this_kg, 0);
          const sumLast = streams_compare.reduce((a, r) => a + r.last_kg, 0);
          const monthDelta = sumLast > 0 ? Math.round(((sumThis - sumLast) / sumLast) * 100) : (sumThis > 0 ? 100 : 0);
          return (
            <div className="hbt-card p-6" data-testid="guide-stream-compare">
              <div className="flex items-start justify-between gap-4 mb-1">
                <div>
                  <div className="hbt-label flex items-center gap-1.5"><Recycle className="w-3 h-3" style={{ color: "var(--forest)" }} /> Your estate this month</div>
                  <h3 className="font-display font-semibold text-2xl md:text-3xl mt-1">What you sorted — stream by stream</h3>
                </div>
                {sumLast > 0 && (
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">vs last month</div>
                    <div className="font-display text-xl font-semibold font-mono-num" style={{ color: monthDelta >= 0 ? "#2C6E49" : "#C85A3C" }}>
                      {monthDelta >= 0 ? "+" : ""}{monthDelta}%
                    </div>
                  </div>
                )}
              </div>
              <p className="text-sm text-neutral-500 mb-5">
                Every green bag you sort shows up here. Compare what the estate has put out so far in <b>{this_month?.period_label}</b> against all of <b>{last_month?.period_label}</b>.
              </p>

              {/* Header row */}
              <div className="grid grid-cols-[minmax(110px,1.1fr)_2fr_80px] md:grid-cols-[minmax(130px,1.2fr)_2fr_90px_90px_70px] gap-3 text-[10px] uppercase tracking-widest text-neutral-500 pb-2 border-b" style={{ borderColor: "var(--border)" }}>
                <div>Stream</div>
                <div>This vs last</div>
                <div className="text-right">This</div>
                <div className="hidden md:block text-right">Last</div>
                <div className="hidden md:block text-right">Δ</div>
              </div>

              <div className="space-y-3 mt-3">
                {streams_compare.map((row) => {
                  const diff = row.this_kg - row.last_kg;
                  const pct = row.last_kg > 0 ? Math.round((diff / row.last_kg) * 100) : (row.this_kg > 0 ? 100 : 0);
                  const thisPct = Math.max(2, Math.round((row.this_kg / maxKg) * 100));
                  const lastPct = Math.max(2, Math.round((row.last_kg / maxKg) * 100));
                  const positive = diff > 0;
                  const neutral = diff === 0 && row.this_kg === 0 && row.last_kg === 0;
                  return (
                    <div key={row.key} className="grid grid-cols-[minmax(110px,1.1fr)_2fr_80px] md:grid-cols-[minmax(130px,1.2fr)_2fr_90px_90px_70px] gap-3 items-center" data-testid={`guide-stream-row-${row.key}`}>
                      <div className="text-sm font-medium truncate" title={row.name}>{row.name}</div>
                      <div className="space-y-1.5">
                        <div className="h-2 rounded-full bg-neutral-100 overflow-hidden" title={`This month ${row.this_kg} ${row.unit}`}>
                          <div className="h-full transition-all" style={{ width: `${thisPct}%`, background: "#2C6E49" }} />
                        </div>
                        <div className="h-2 rounded-full bg-neutral-100 overflow-hidden" title={`Last month ${row.last_kg} ${row.unit}`}>
                          <div className="h-full transition-all" style={{ width: `${lastPct}%`, background: "#2C6E49", opacity: 0.35 }} />
                        </div>
                      </div>
                      <div className="text-right font-mono-num text-sm font-semibold">{row.this_kg} <span className="text-neutral-400 font-normal">{row.unit}</span></div>
                      <div className="hidden md:block text-right font-mono-num text-xs text-neutral-500">{row.last_kg} <span className="text-neutral-400">{row.unit}</span></div>
                      <div className="hidden md:block text-right text-xs font-mono-num" style={{ color: neutral ? "#9AA39A" : positive ? "#2C6E49" : diff < 0 ? "#C85A3C" : "#9AA39A" }}>
                        {row.last_kg === 0 && row.this_kg > 0 ? "new" : row.this_kg === 0 && row.last_kg === 0 ? "—" : `${pct > 0 ? "+" : ""}${pct}%`}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex items-center gap-4 pt-4 mt-4 border-t text-[11px] text-neutral-500" style={{ borderColor: "var(--border)" }}>
                <div className="flex items-center gap-1.5">
                  <span className="inline-block w-5 h-1.5 rounded-full" style={{ background: "#2C6E49" }} /> {this_month?.period_label || "This month"}
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="inline-block w-5 h-1.5 rounded-full" style={{ background: "#2C6E49", opacity: 0.35 }} /> {last_month?.period_label || "Last month"}
                </div>
                <div className="ml-auto text-right">
                  <span className="font-mono-num">{sumThis.toFixed(1)} kg</span> this · <span className="font-mono-num text-neutral-400">{sumLast.toFixed(1)} kg</span> last
                </div>
              </div>

              <p className="text-xs text-neutral-500 mt-4">
                {monthDelta > 0
                  ? "Nice work — more waste is reaching the sorting line this month. Keep going. 💚"
                  : monthDelta < 0
                    ? "Still time to shift the numbers before month-end. Every rinsed bottle counts."
                    : "A fresh month just started — your green-bag habits will show up right here."}
              </p>
            </div>
          );
        })()}

        {/* Last month's report card */}
        {last_month && last_month.total_kg > 0 && (
          <div className="hbt-card p-6" data-testid="guide-last-month">
            <div className="hbt-label mb-2">{last_month.period_label} · your estate</div>
            <div className="flex items-baseline gap-4 flex-wrap">
              <div>
                <div className="font-display font-semibold text-3xl md:text-4xl">{last_month.total_kg.toLocaleString()} <span className="text-lg text-neutral-500">kg</span></div>
                <div className="text-sm text-neutral-500">total waste collected</div>
              </div>
              <div className="ml-auto">
                <div className="font-display font-semibold text-3xl md:text-4xl" style={{ color: last_month.diversion_rate >= 50 ? "#2C6E49" : "#B58900" }}>{last_month.diversion_rate}%</div>
                <div className="text-sm text-neutral-500">recycled + composted</div>
              </div>
            </div>
            {last_month.top_streams?.length > 0 && (
              <div className="mt-5 space-y-2">
                <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Top streams</div>
                {last_month.top_streams.map((s) => {
                  const pct = Math.max(3, Math.round((s.kg / last_month.top_streams[0].kg) * 100));
                  return (
                    <div key={s.name} className="grid grid-cols-[minmax(140px,1fr)_2fr_90px] gap-3 items-center">
                      <div className="text-sm truncate">{s.name}</div>
                      <div className="h-2 rounded-full bg-neutral-100 overflow-hidden"><div className="h-full" style={{ width: `${pct}%`, background: "#2C6E49" }} /></div>
                      <div className="text-right text-sm font-mono-num font-semibold">{s.kg.toLocaleString()} {s.unit}</div>
                    </div>
                  );
                })}
              </div>
            )}
            <p className="text-xs text-neutral-500 mt-4">Small changes at home move these numbers next month. Every green bag counts. 💚</p>
          </div>
        )}

        {/* FAQ */}
        {guide.faqs?.length > 0 && (
          <div className="hbt-card p-6" data-testid="guide-faqs">
            <div className="flex items-center gap-2 mb-4">
              <HelpCircle className="w-4 h-4" style={{ color: "var(--forest)" }} />
              <div className="hbt-label">Frequently asked</div>
            </div>
            <div className="divide-y" style={{ borderColor: "var(--border)" }}>
              {guide.faqs.map((f, i) => (
                <details key={i} className="py-3 group" data-testid={`faq-${i}`}>
                  <summary className="font-medium cursor-pointer text-sm flex items-center justify-between">
                    <span>{f.question}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-neutral-400 group-open:rotate-90 transition-transform" />
                  </summary>
                  <p className="mt-2 text-sm text-neutral-600 leading-relaxed pr-6">{f.answer}</p>
                </details>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-neutral-500 py-8">
          <div>This guide is powered by <Link to="/" className="hover:underline text-[var(--forest)] font-semibold">HBT Waste Management</Link></div>
          <div className="mt-1">Together we make Eswatini a cleaner place.</div>
        </div>
      </div>
    </div>
  );
}
